import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import api from "../api";
import { Calendar as CalIcon, Copy, ExternalLink, CheckCircle2, AlertTriangle, Settings as SettingsIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import NavOrderEditor from "../components/NavOrderEditor";
import MicrosoftConnect from "../components/MicrosoftConnect";
import TwoFactorSetup from "../components/TwoFactorSetup";

export default function SettingsPage() {
    const { lang, user } = useAuth();
    const [ical, setIcal] = useState(null);
    const [superchatOk, setSuperchatOk] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/ical/my-feed");
            setIcal(data);
        } catch { /* ignore */ }
        if (user?.role === "admin" || user?.role === "master") {
            try {
                const { data } = await api.get("/superchat/status");
                setSuperchatOk(data);
            } catch { /* ignore */ }
        }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    const copy = (txt) => {
        navigator.clipboard.writeText(txt);
        toast.success(t(lang, "copied"));
    };

    return (
        <div className="px-4 sm:px-8 py-8 max-w-3xl" data-testid="settings-page">
            <div className="mb-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5">
                    <SettingsIcon className="w-3 h-3" /> {t(lang, "settings")}
                </p>
                <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold mt-1">{t(lang, "settings")}</h1>
            </div>

            <NavOrderEditor />

            <div className="mb-4">
                <TwoFactorSetup lang={lang} />
            </div>

            <MicrosoftConnect lang={lang} />

            {/* iCal Sync */}
            <section className="p-5 bg-white border border-neutral-200 rounded-lg mb-4" data-testid="ical-section">
                <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-2">
                    <CalIcon className="w-5 h-5" /> {t(lang, "icalSync")}
                </h2>
                <p className="text-sm text-neutral-600 mb-4">{t(lang, "icalDescr")}</p>
                {ical && (
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Apple Calendar / iOS</label>
                            <div className="flex items-center gap-2 mt-1.5">
                                <Input value={ical.webcal} readOnly className="font-mono text-xs" data-testid="ical-webcal" />
                                <Button onClick={() => copy(ical.webcal)} variant="outline" size="sm" data-testid="copy-webcal">
                                    <Copy className="w-3.5 h-3.5" />
                                </Button>
                                <a href={ical.webcal} data-testid="open-webcal" className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100">
                                    <ExternalLink className="w-3 h-3" /> {t(lang, "openInApple")}
                                </a>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Google Calendar / Android (HTTPS)</label>
                            <div className="flex items-center gap-2 mt-1.5">
                                <Input value={ical.https} readOnly className="font-mono text-xs" data-testid="ical-https" />
                                <Button onClick={() => copy(ical.https)} variant="outline" size="sm" data-testid="copy-https">
                                    <Copy className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                            <p className="text-[11px] text-neutral-500 mt-1">
                                {lang === "de" ? "Google Calendar → Andere Kalender → URL hinzufügen → einfügen" : "Google Calendar → Other calendars → From URL → paste"}
                            </p>
                        </div>
                    </div>
                )}
            </section>

            {/* Superchat status (admin/master only) */}
            {(user?.role === "admin" || user?.role === "master") && (
                <section className="p-5 bg-white border border-neutral-200 rounded-lg" data-testid="superchat-section">
                    <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-2">
                        💬 {t(lang, "superchat")}
                    </h2>
                    <p className="text-sm text-neutral-600 mb-4">
                        {lang === "de" ? "Kunden-Kommunikation (WhatsApp/SMS/E-Mail/Instagram) über die Superchat-Plattform." : "Customer communication (WhatsApp/SMS/Email/Instagram) via Superchat."}
                    </p>
                    {superchatOk && (
                        <div className={`p-3 rounded-md border flex items-start gap-2 ${superchatOk.ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="superchat-status-badge">
                            {superchatOk.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                            <div className="flex-1">
                                <p className="font-semibold text-sm">{superchatOk.configured ? t(lang, "superchatConfigured") : t(lang, "superchatNotConfigured")}</p>
                                {!superchatOk.ok && superchatOk.detail && (
                                    <p className="text-xs text-neutral-700 mt-1">{superchatOk.detail}</p>
                                )}
                                {!superchatOk.ok && (
                                    <p className="text-[11px] text-neutral-600 mt-2">
                                        {lang === "de" ? "Hinweis: Die exakte Superchat API-Basis-URL und das Auth-Header-Format müssen in der Superchat-Dokumentation bestätigt werden (Backend .env: SUPERCHAT_API_BASE_URL)." : "Note: Verify exact Superchat API base URL + auth header format with Superchat docs."}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="mt-4">
                        <a href="/superchat" data-testid="open-superchat" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-neutral-300 hover:bg-neutral-50 text-sm">
                            <ExternalLink className="w-3.5 h-3.5" /> {t(lang, "customerComms")} →
                        </a>
                    </div>
                </section>
            )}
        </div>
    );
}
